'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import type { CatalogItem, CategoryNode } from './header-data';

import Link from '@eliadmin/core/src/components/link';
import { CmsIcon } from '@eliadmin/core/src/components/dlz/ui/cms-image';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const ROW =
  'flex w-full items-center gap-3 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F1012]';

/**
 * The catalogue behind „Kategórie“ of the mobile navbar (Figma „Open kategorie 1.–3. level“
 * 60391:76414, 77961, 78216): the catalogue entries with their icons, then one level per screen with
 * „‹ name“ back, „Celá kategória“ and the children; a node without children is a link. The content
 * of the navbar's non-modal dialog, from `top` (the header bottom) to the screen bottom – the navbar
 * covers its last 66 px, hence the bottom padding.
 */
export function CategoriesPanel({
  catalog,
  top,
  onNavigate,
}: {
  catalog: CatalogItem[];
  top: number;
  onNavigate: () => void;
}) {
  const { t } = useTranslations();
  const content = useRef<HTMLDivElement>(null);
  const [trail, setTrail] = useState<CategoryNode[]>([]);
  const current = trail.at(-1);
  const open = (node: CategoryNode) => setTrail(nodes => [...nodes, node]);

  // Every level starts at its top.
  useLayoutEffect(() => {
    content.current?.scrollTo({ top: 0 });
  }, [current]);

  return (
    <Dialog.Content
      ref={content}
      aria-describedby={undefined}
      style={{ top }}
      onClick={event => {
        // A link to the current page does not change the pathname.
        if ((event.target as HTMLElement).closest('a')) onNavigate();
      }}
      className="fixed inset-x-0 bottom-0 z-30 flex flex-col gap-4 overflow-y-auto overscroll-contain bg-white px-4 pb-[calc(82px+env(safe-area-inset-bottom))] pt-4 focus:outline-none md:px-6 lg:hidden"
    >
      <Dialog.Title className={cn('dlz-h3 text-[#0F1012]', current && 'sr-only')}>
        {t('text176')}
      </Dialog.Title>
      {current ? (
        <Level node={current} onBack={() => setTrail(nodes => nodes.slice(0, -1))} onOpen={open} />
      ) : (
        <ul className="divide-y divide-[#E2E4E7] border-b border-[#E2E4E7]">
          {catalog.map(item => (
            <li key={item.id}>
              <Row node={item} onOpen={open} className="dlz-body-m-medium">
                <CmsIcon image={item.icon} className="size-6" />
                <span className="min-w-0 flex-1">{item.name}</span>
                <Icon name="caret-right" size={20} className="text-[#D7191F]" />
              </Row>
            </li>
          ))}
        </ul>
      )}
    </Dialog.Content>
  );
}

/** Level 2 and 3: back to the parent level, the whole category, the children. */
function Level({
  node,
  onBack,
  onOpen,
}: {
  node: CategoryNode;
  onBack: () => void;
  onOpen: (node: CategoryNode) => void;
}) {
  const { t } = useTranslations();

  return (
    <>
      <button
        type="button"
        aria-label={`${t('text1026')}: ${node.name}`}
        onClick={onBack}
        className="dlz-h5 flex w-full items-center gap-2.5 rounded-sm text-left text-[#D7191F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
      >
        <Icon name="caret-left" size={20} />
        <span className="min-w-0 flex-1">{node.name}</span>
      </button>
      <ul className="divide-y divide-[#E2E4E7]">
        <li>
          <Link href={node.href} prefetch={false} className={cn(ROW, 'dlz-body-m-medium')}>
            <span className="min-w-0 flex-1 text-[#D7191F]">{t('text3475')}</span>
            <Icon name="chevron-right" size={18} className="text-[#D7191F]" />
          </Link>
        </li>
        {node.children.map(child => (
          <li key={child.id}>
            <Row node={child} onOpen={onOpen} className="dlz-body-m">
              <span className="min-w-0 flex-1">{child.name}</span>
              <Icon name="chevron-right" size={18} className="text-[#D7191F]" />
            </Row>
          </li>
        ))}
      </ul>
    </>
  );
}

/** A node with children opens its level, a leaf links to its category. */
function Row({
  node,
  onOpen,
  className,
  children,
}: {
  node: CategoryNode;
  onOpen: (node: CategoryNode) => void;
  className: string;
  children: ReactNode;
}) {
  const classes = cn(ROW, 'text-[#0F1012]', className);
  if (!node.children.length) {
    return (
      <Link href={node.href} prefetch={false} className={classes}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(node)} className={classes}>
      {children}
    </button>
  );
}
